import {
  WeeklyUpdateCreateDocument,
  WeeklyUpdateDeleteDocument,
  WeeklyUpdateDocumentDownloadDocument,
  WeeklyUpdateUpdateDocument,
  WeeklyUpdateDocumentUploadDocument,
  WeeklyUpdateGetByIdDocument,
  WeeklyUpdateForHomepageByIdDocument,
  WeeklyUpdatesByUserDocument,
  WeeklyUpdatesPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { IFile, NumberFiltersType } from "api/services/types";
import { Optional, PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  GetWeeklyUpdatesReturnType,
  ICreateWeeklyUpdateVars,
  IUpdateWeeklyUpdateVars,
  IWeeklyUpdateDocumentModel,
  IWeeklyUpdateModel,
  IWeeklyUpdatesPaginatedResponse,
  IWeeklyUpdatesPaginatedVars,
  WeeklyUpdateCreateUpdateReturnType,
  WeeklyUpdateForHomepageReturnType,
} from "./types";

export async function getWeeklyUpdatesPaginated(
  variables: IWeeklyUpdatesPaginatedVars
): Promise<IWeeklyUpdatesPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WeeklyUpdatesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.weeklyUpdatesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createWeeklyUpdate(
  variables: ICreateWeeklyUpdateVars
): Promise<Optional<WeeklyUpdateCreateUpdateReturnType>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WeeklyUpdateCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "WeeklyUpdate",
      fieldNames: ["weeklyUpdatesPaginated", "weeklyUpdatesByUser"],
    });

    return res?.data?.weeklyUpdateCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateWeeklyUpdate(
  variables: IUpdateWeeklyUpdateVars
): Promise<Optional<WeeklyUpdateCreateUpdateReturnType>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WeeklyUpdateUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "WeeklyUpdate",
      id: variables.weeklyUpdateId.toString(),
      fieldNames: [
        "weeklyUpdatesPaginated",
        "weeklyUpdatesByUser",
        "weeklyUpdateGetById",
        "weeklyUpdateViewForInvestor",
      ],
    });

    return res?.data?.weeklyUpdateUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getWeeklyUpdate(
  weeklyUpdateId: number
): Promise<PartialNullable<IWeeklyUpdateModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WeeklyUpdateGetByIdDocument,
      variables: { weeklyUpdateId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.weeklyUpdateGetById;
  } catch (error) {
    throw error;
  }
}

export async function getWeeklyUpdates(
  productFundId?: NumberFiltersType
): Promise<GetWeeklyUpdatesReturnType[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WeeklyUpdatesByUserDocument,
      variables: { productFundId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.weeklyUpdatesByUser ?? [];
  } catch (error) {
    throw error;
  }
}

export async function deleteWeeklyUpdate(
  weeklyUpdateId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WeeklyUpdateDeleteDocument,
      variables: { weeklyUpdateId },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "WeeklyUpdate",
      id: weeklyUpdateId.toString(),
      fieldNames: ["weeklyUpdatesPaginated", "weeklyUpdatesByUser"],
    });

    return Boolean(res?.data?.weeklyUpdateDelete);
  } catch (error) {
    throw error;
  }
}

export async function uploadWeeklyUpdateDocument(
  variables: IFile
): Promise<Optional<IWeeklyUpdateDocumentModel<number>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WeeklyUpdateDocumentUploadDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.relatedEntityId) {
      executeEviction(client, {
        typename: "WeeklyUpdate",
        id: variables.relatedEntityId.toString(),
        fieldNames: [
          "weeklyUpdateGetById",
          "weeklyUpdateViewForInvestor",
          "weeklyUpdatesPaginated",
          "weeklyUpdatesByUser",
        ],
      });
    }

    return res?.data?.weeklyUpdateDocumentUpload;
  } catch (error) {
    throw error;
  }
}

export async function downloadWeeklyUpdateDocument(
  weeklyUpdateDocumentId: number
): Promise<Optional<string>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WeeklyUpdateDocumentDownloadDocument,
      variables: { weeklyUpdateDocumentId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.weeklyUpdateDocumentDownload;
  } catch (error) {
    throw error;
  }
}

export async function getWeeklyUpdateForHomepage(
  weeklyUpdateId: number
): Promise<WeeklyUpdateForHomepageReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WeeklyUpdateForHomepageByIdDocument,
      variables: { weeklyUpdateId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.weeklyUpdateViewForInvestor;
  } catch (error) {
    throw error;
  }
}
